/* eslint-disable react-hooks/exhaustive-deps */
import { useMemo, useState } from 'react'
import { useSelector, shallowEqual } from 'react-redux'
import { filterEcommerce } from '~/utils'
import { selectPosition } from '~/selectors'

export function useDataEcommerce(dataEcommerce, configEcommerce) {
  const [position] = useSelector(selectPosition, shallowEqual)
  const [mergeOtherOffers, setMergeOtherOffers] = useState([])

  const dataOffers = dataEcommerce?.offers ?? []

  const isEcommerceGoogle = configEcommerce?.buyOnline === 'ecommerceGoogle'
  const isViewLimited = Boolean(configEcommerce?.limitViews)
  const isViewOnlyStoresManuals = Boolean(configEcommerce?.checkStoreAddManually)
  const isViewOnlyStoresFeatured = Boolean(configEcommerce?.checkStoreFeatured)

  const isEnableAlertPrice = Boolean(configEcommerce?.showAlertPrice)
  const priceMonitoring = configEcommerce?.priceMonitoring || []

  const storesManuals = filterEcommerce(configEcommerce?.storesAddManually, position) || []
  const storesFeatured = configEcommerce?.storesFeatured || []
  const storesBlocked = configEcommerce?.blackList || []
  const showGoogleResultsFirst = Boolean(configEcommerce?.showGoogleResultsFirst) && isEcommerceGoogle

  const hasShowPrice = Boolean(configEcommerce?.showProductValue)

  const hasStoresManuals = useMemo(() => {
    return isEcommerceGoogle && storesManuals.length > 0
  }, [isEcommerceGoogle, storesManuals])

  const hasStoresBlocked = useMemo(() => {
    return isEcommerceGoogle && storesBlocked.length > 0
  }, [isEcommerceGoogle, storesBlocked.length])

  const hasStoresFeatured = useMemo(() => {
    return (
      isEcommerceGoogle &&
      dataOffers.filter(({ name }) =>
        storesFeatured.some((store) => store?.name.toLowerCase().includes(name.toLowerCase())),
      ).length > 0
    )
  }, [isEcommerceGoogle, storesFeatured, dataOffers])

  const normalize = (str) => (str || '').toLowerCase().trim()

  const storesFeaturedNames = storesFeatured.map((store) => normalize(store.name)).filter(Boolean)

  const dataOffersFeatured = storesFeatured
    .map(({ name }) => dataOffers.find((store) => store?.name.toLowerCase().includes(name.toLowerCase())))
    .filter((store) => Boolean(store))

  const dataOffersFeaturedStoreNames = dataOffersFeatured.map((store) => normalize(store.name))

  const visibleManuals = useMemo(() => {
    return storesManuals.filter((store) => !dataOffersFeaturedStoreNames.includes(normalize(store.name)))
  }, [storesManuals, dataOffersFeaturedStoreNames])

  const filteredOffers = dataOffers
    .filter(({ name }) =>
      isEcommerceGoogle ? !storesBlocked.some((store) => normalize(store?.name) === normalize(name)) : [],
    )
    .filter(({ name }) =>
      isEcommerceGoogle
        ? !storesFeaturedNames.includes(normalize(name)) && !dataOffersFeaturedStoreNames.includes(normalize(name))
        : [],
    )

  const totalOffers = useMemo(() => {
    if (isViewOnlyStoresFeatured && isViewOnlyStoresManuals) return visibleManuals.length + dataOffersFeatured.length

    if (isViewOnlyStoresFeatured) return dataOffersFeatured.length
    if (isViewOnlyStoresManuals) return visibleManuals.length

    return filteredOffers.length + visibleManuals.length + dataOffersFeatured.length + mergeOtherOffers.length
  }, [
    isViewOnlyStoresFeatured,
    isViewOnlyStoresManuals,
    visibleManuals,
    dataOffersFeatured,
    filteredOffers,
    mergeOtherOffers,
  ])

  const offerMinMax = useMemo(() => {
    const parsePrice = (price) => {
      if (typeof price === 'string') {
        price = price.replace(/[^\d.,-]/g, '').replace(',', '.')
      }
      return Number(price)
    }

    const allOffers = [...(filteredOffers || []), ...(dataOffersFeatured || []), ...(mergeOtherOffers || [])]

    const prices = allOffers.map((o) => parsePrice(o.price)).filter((price) => !isNaN(price))
    const min = prices.length ? Math.min(...prices) : 0
    const max = prices.length ? Math.max(...prices) : 0

    const format = (value) =>
      Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value)
    return { min: format(min), max: format(max) }
  }, [filteredOffers, dataOffersFeatured, mergeOtherOffers])

  const showProductValue = useMemo(() => {
    return isEcommerceGoogle && hasShowPrice
  }, [isEcommerceGoogle, hasShowPrice])

  const isEcommerceNotFound = useMemo(() => {
    if (isViewLimited) {
      if (isViewOnlyStoresManuals && hasStoresManuals) return false

      if (dataOffers.length && isViewOnlyStoresFeatured && hasStoresFeatured) return false
    }

    if (hasStoresManuals) return false

    if (filteredOffers.length) return false

    return true
  }, [
    dataOffers,
    hasStoresManuals,
    hasStoresFeatured,
    filteredOffers,
    isViewLimited,
    isViewOnlyStoresManuals,
    isViewOnlyStoresFeatured,
  ])

  const showOffers = useMemo(() => {
    if (isViewOnlyStoresFeatured && isViewOnlyStoresManuals) {
      return hasStoresManuals && dataOffersFeatured.length > 1
    }

    if (isViewOnlyStoresManuals) return false
    if (isViewOnlyStoresFeatured) return dataOffersFeatured.length > 1

    return (hasStoresManuals && dataOffersFeatured.length > 1) || filteredOffers.length > 1
  }, [isViewOnlyStoresFeatured, isViewOnlyStoresManuals, dataOffersFeatured, hasStoresManuals, filteredOffers])

  return {
    dataOffers,
    dataOffersFeatured,
    filteredOffers,
    hasStoresFeatured,
    hasStoresManuals,
    hasStoresBlocked,
    isEcommerceGoogle,
    isEcommerceNotFound,
    isEnableAlertPrice,
    isViewLimited,
    isViewOnlyStoresManuals,
    isViewOnlyStoresFeatured,
    offerMinMax,
    mergeOtherOffers,
    setMergeOtherOffers,
    priceMonitoring,
    showOffers,
    showProductValue,
    showGoogleResultsFirst,
    storesFeatured,
    storesManuals,
    storesBlocked,
    totalOffers,
  }
}
